/** Provides a horizontal week-based overview of tasks organized by day and planning regime. */
import { format, isSameDay, isToday } from 'date-fns';
import { Box } from '@mui/material';
import TimeSlotCell from '@/features/planning/components/horizon/TimeSlotCell';
import {
  MAX_PLAN_CAPACITY_HOURS,
  PLANNING_REGIMES,
} from '@/features/planning/config/planningConfig';
import { useT } from '@/i18n/useT';
import {
  getDateFnsLocale,
  getShortWeekdayPattern,
} from '@/i18n/getDateFnsLocale';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import {
  filterDayWorkUnits,
  getCapacityDurationMinutes,
  isSchedulableTask,
} from '@/features/tasks/utils/scheduling/taskSchedulingUtils';

const MINUTES_PER_HOUR = 60;
const REGIME_CAPACITY_MINUTES = MAX_PLAN_CAPACITY_HOURS * MINUTES_PER_HOUR;
import { useGCalEventsForRange } from '@/features/planning/hooks/use-gcal-events-for-range';
import { filterGCalEventsForDay } from '@/features/planning/utils/gcalHorizonUtils';

const WeekView = ({ weekDays, tasks }) => {
  const t = useT();
  const dateFnsLocale = getDateFnsLocale();
  const weekdayPattern = getShortWeekdayPattern();
  const { tasksMap } = useTasksContext();
  const REGIME_KEYS = [
    'weekdaycell.morning',
    'weekdaycell.afternoon',
    'weekdaycell.evening',
  ];
  const gcalEvents = useGCalEventsForRange(
    weekDays[0],
    weekDays[weekDays.length - 1],
  );

  return (
    <Box
      data-testid="week-scroll"
      tabIndex={0}
      role="region"
      aria-label={t('plan.weekRegion', 'Week overview')}
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'grid',
        gridTemplateColumns: `repeat(${weekDays.length}, minmax(0, 1fr))`,
        // header, allday, 3 regimes
        gridTemplateRows: 'auto auto 1fr 1fr 1fr',
        gridAutoFlow: 'column',
        columnGap: '8px',
        rowGap: 0,
        overflowY: 'auto',
      }}
    >
      {weekDays.map((date, dayIndex) => {
        const tasksForDay = filterDayWorkUnits(
          tasks.filter((t) => {
            if (!t.date || t.completed) return false;
            if (!isSchedulableTask(t, tasksMap)) return false;
            const taskDate =
              t.date instanceof Date
                ? t.date
                : t.date.toDate
                  ? t.date.toDate()
                  : new Date(t.date);
            return isSameDay(taskDate, date);
          }),
          tasksMap,
        );
        const gcalEventsForDay = filterGCalEventsForDay(gcalEvents, date);

        const dayName = format(date, weekdayPattern, {
          locale: dateFnsLocale,
        });
        const dayNumber = date.getDate();
        const today = isToday(date);
        const gridColumn = dayIndex + 1;

        return (
          <Box key={date.toISOString()} sx={{ display: 'contents' }}>
            {/* Day header (row 1) */}
            <Box
              sx={{
                gridColumn,
                gridRow: 1,
                bgcolor: today ? 'primary.main' : 'background.subtle',
                color: today ? 'primary.contrastText' : 'text.primary',
                border: 1,
                borderColor: 'divider',
                borderRadius: '4px 4px 0 0',
                p: '6px',
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  fontSize: '0.72em',
                  fontWeight: 700,
                }}
              >
                {dayName} {dayNumber}
              </Box>
            </Box>

            {/* Allday (row 2) */}
            <Box sx={{ gridColumn, gridRow: 2 }}>
              <TimeSlotCell
                title=""
                timeSlot="allday"
                date={date}
                tasks={tasksForDay}
                gcalEvents={gcalEventsForDay}
                regime={null}
              />
            </Box>

            {PLANNING_REGIMES.map((regime, index) => {
              const regimeLabel = t(
                REGIME_KEYS[index] || 'weekdaycell.morning',
              );
              const timeSlotId = `regime-${index}`;
              const regimeTasks = tasksForDay.filter(
                (task) =>
                  task.time &&
                  task.time >= regime.start &&
                  task.time < regime.end,
              );
              const regimePlannedMins = regimeTasks.reduce(
                (sum, task) =>
                  sum + getCapacityDurationMinutes(task, tasksForDay, tasksMap),
                0,
              );
              return (
                <Box key={timeSlotId} sx={{ gridColumn, gridRow: 3 + index }}>
                  <TimeSlotCell
                    title={regimeLabel}
                    timeSlot={timeSlotId}
                    date={date}
                    tasks={tasksForDay}
                    gcalEvents={gcalEventsForDay}
                    regime={regime}
                    plannedMins={regimePlannedMins}
                    maxMins={REGIME_CAPACITY_MINUTES}
                  />
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};

export default WeekView;
